import {
  Calendar,
  CalendarDays,
  Check,
  ChevronDown,
  Expand,
  Grid3x3,
  List,
  Minimize2,
  Search,
  ZoomIn,
} from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { CalendarTheme } from "@/domains/appointment/appointments/config/calendar-config";
import { SCALE_DEFINITIONS } from "@/domains/appointment/appointments/config/calendar-config";
import { useCalendarStateStore } from "@/domains/appointment/calendar/store/use-calendar-state-store";
import { useConfig } from "@/hooks/use-config";
import { useNewCalendarUIStore } from "../store/use-new-calendar-ui-store";
import type { NewCalendarViewMode } from "../types";
import { CalendarWorkerIcon } from "./calendar-worker-icon";

interface UnifiedViewConfigDropdownProps {
  className?: string;
  isMobile?: boolean;
}

// View mode icons
const VIEW_MODE_ICONS = {
  list: List,
  daily: Calendar,
  "3-day": Grid3x3,
  week: Calendar,
  month: Calendar,
  worker: CalendarWorkerIcon,
} as const;

// Scale options with icons
const SCALE_ICONS = {
  default: CalendarDays,
  expanded: ZoomIn,
  detailed: Search,
} as const;

export function UnifiedViewConfigDropdown({
  className = "",
  isMobile = false,
}: UnifiedViewConfigDropdownProps) {
  const { t } = useTranslation("newCalendar");
  const { viewMode, setViewMode, multiServiceExpanded, toggleMultiServiceExpansion } =
    useNewCalendarUIStore();
  const { timeScale, setTimeScale } = useCalendarStateStore();
  const { value: workerAssignmentEnabled = false } = useConfig(
    "salon.appointment_worker_assignment_enabled"
  );

  // Group view modes by type
  const listViewMode = {
    mode: "list" as NewCalendarViewMode,
    label: t("viewModes.list.label"),
    description: t("viewModes.list.description"),
  };

  const calendarViewModes: {
    mode: NewCalendarViewMode;
    label: string;
    description: string;
  }[] = [
    {
      mode: "daily",
      label: t("viewModes.daily.label"),
      description: t("viewModes.daily.description"),
    },
    {
      mode: "3-day",
      label: t("viewModes.threeDays.label"),
      description: t("viewModes.threeDays.description"),
    },
    // Week and month views only available on desktop
    ...(!isMobile
      ? [
          {
            mode: "week" as NewCalendarViewMode,
            label: t("viewModes.week.label"),
            description: t("viewModes.week.description"),
          },
          {
            mode: "month" as NewCalendarViewMode,
            label: t("viewModes.month.label"),
            description: t("viewModes.month.description"),
          },
        ]
      : []),
    ...(workerAssignmentEnabled
      ? [
          {
            mode: "worker" as NewCalendarViewMode,
            label: t("viewModes.worker.label"),
            description: t("viewModes.worker.description"),
          },
        ]
      : []),
  ];

  // Determine if current view mode is a calendar view (not list)
  const isCalendarViewMode = ["daily", "3-day", "week", "month", "worker"].includes(viewMode);
  const showExpandCollapse = viewMode === "list" && workerAssignmentEnabled;
  const showZoomLevel = isCalendarViewMode;

  const handleViewModeChange = (newViewMode: NewCalendarViewMode) => {
    setViewMode(newViewMode);
  };

  const handleZoomLevelChange = (newScale: CalendarTheme) => {
    setTimeScale(newScale);
  };

  const getScaleLabel = (scale: CalendarTheme): string => {
    const labels = {
      default: t("config.timeScale.default", "Default"),
      expanded: t("config.timeScale.expanded", "Expanded"),
      detailed: t("config.timeScale.detailed", "Detailed"),
    };
    return labels[scale] || scale;
  };

  // Get current view mode info
  const allViewModes = [listViewMode, ...calendarViewModes];
  const currentViewModeInfo = allViewModes.find((vm) => vm.mode === viewMode);
  const CurrentViewIcon = VIEW_MODE_ICONS[viewMode];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-9 ${isMobile ? "px-2" : "px-3 gap-2"} ${className}`}
        >
          <CurrentViewIcon />
          {!isMobile && <span>{currentViewModeInfo?.label || "View"}</span>}
          {!isMobile && <ChevronDown className="h-3 w-3" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {/* List View Mode */}
        <DropdownMenuLabel>{t("viewModes.listMode.title", "List Mode")}</DropdownMenuLabel>

        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            handleViewModeChange(listViewMode.mode);
          }}
          onSelect={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <List className="h-4 w-4" />
              <span className={viewMode === "list" ? "font-medium" : ""}>{listViewMode.label}</span>
            </div>
            {viewMode === "list" && (
              <div className="flex items-center gap-1">
                <Check className="h-3 w-3 text-green-600" />
                <span className="text-xs text-green-600 font-medium">
                  {t("config.active", "Active")}
                </span>
              </div>
            )}
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Calendar View Modes */}
        <DropdownMenuLabel>
          {t("viewModes.calendarModes.title", "Calendar Modes")}
        </DropdownMenuLabel>

        {calendarViewModes.map((viewModeOption) => {
          const ViewIcon = VIEW_MODE_ICONS[viewModeOption.mode];
          const isSelected = viewMode === viewModeOption.mode;

          return (
            <DropdownMenuItem
              key={viewModeOption.mode}
              onClick={(e) => {
                e.preventDefault();
                handleViewModeChange(viewModeOption.mode);
              }}
              onSelect={(e) => e.preventDefault()}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <ViewIcon className="h-4 w-4" />
                  <span className={isSelected ? "font-medium" : ""}>{viewModeOption.label}</span>
                </div>
                {isSelected && (
                  <div className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">
                      {t("config.active", "Active")}
                    </span>
                  </div>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}

        {/* Configuration Section */}
        {(showExpandCollapse || showZoomLevel) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("config.title", "Configuration")}</DropdownMenuLabel>

            {/* Expand/Collapse Toggle for List View */}
            {showExpandCollapse && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  toggleMultiServiceExpansion();
                }}
                onSelect={(e) => e.preventDefault()}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {multiServiceExpanded ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Expand className="h-4 w-4" />
                    )}
                    <span>
                      {multiServiceExpanded
                        ? t("config.collapseServices", "Collapse Services")
                        : t("config.expandServices", "Expand Services")}
                    </span>
                  </div>
                  {multiServiceExpanded && (
                    <div className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-600 font-medium">
                        {t("config.active", "Active")}
                      </span>
                    </div>
                  )}
                </div>
              </DropdownMenuItem>
            )}

            {/* Zoom Level Controls for Calendar Views */}
            {showZoomLevel && (
              <>
                {showExpandCollapse && <DropdownMenuSeparator />}
                <DropdownMenuLabel>{t("config.timeScale.title", "Time Scale")}</DropdownMenuLabel>

                {Object.entries(SCALE_DEFINITIONS).map(([key]) => {
                  const scale = key as CalendarTheme;
                  const isSelected = timeScale === scale;
                  const label = getScaleLabel(scale);
                  const icon = SCALE_ICONS[scale];

                  return (
                    <DropdownMenuItem
                      key={scale}
                      onClick={(e) => {
                        e.preventDefault();
                        handleZoomLevelChange(scale);
                      }}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          {React.createElement(icon, { className: "h-4 w-4" })}
                          <span className={isSelected ? "font-medium" : ""}>{label}</span>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-1">
                            <Check className="h-3 w-3 text-green-600" />
                            <span className="text-xs text-green-600 font-medium">
                              {t("config.active", "Active")}
                            </span>
                          </div>
                        )}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
